import { API_BASE } from "../config/api";
import React, { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes } from "react-router-dom";
import { Helmet } from "react-helmet";
import EventEditor from "./EventEditor";
import {
  FiCalendar,
  FiUser,
  FiLogOut,
  FiArrowUpRight,
  FiRefreshCw,
  FiSearch,
} from "react-icons/fi";

const apiBase = API_BASE;
const states = {
  draft: "Bozza",
  published: "Pubblicato",
  archived: "Archiviato",
};
const badges = {
  draft: "bg-amber-50 text-amber-800",
  published: "bg-emerald-50 text-emerald-800",
  archived: "bg-slate-100 text-slate-600",
};

export default function Dashboard({ session, onLogout }) {
  return (
    <div className="min-h-screen bg-[#f8f6f1] text-[#263b3e] md:flex [&_:focus-visible]:outline [&_:focus-visible]:outline-2 [&_:focus-visible]:outline-offset-4 [&_:focus-visible]:outline-[#a27d45]">
      <Helmet>
        <title>Dashboard | Serena Ferraris</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <aside className="flex flex-col bg-[#263b3e] p-6 text-white md:sticky md:top-0 md:h-screen md:w-64 md:shrink-0">
        <Link to="/" className="font-serif text-2xl">
          Serena Ferraris
        </Link>
        <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#d6c3a5]">
          Studio · Area admin
        </p>
        <nav
          aria-label="Navigazione amministrazione"
          className="my-8 flex gap-2 md:flex-col"
        >
          {[
            { to: "/admin/events", label: "Eventi première", icon: FiCalendar },
            { to: "/admin/profile", label: "Il mio account", icon: FiUser },
          ].map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-4 py-3 text-sm transition-colors ${isActive ? "bg-[#f8f6f1] text-[#263b3e]" : "text-white hover:bg-white/10"}`
              }
            >
              <Icon aria-hidden="true" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto border-t border-white/20 pt-5">
          <p className="truncate text-sm font-medium">{session.user.name}</p>
          <p className="mt-1 text-xs text-[#d6c3a5]">Amministratore</p>
          <button
            onClick={onLogout}
            className="mt-4 flex items-center gap-2 rounded py-2 text-sm text-white/80 hover:text-white"
          >
            <FiLogOut aria-hidden="true" />
            Esci dall’area admin
          </button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 p-6 lg:p-12">
        <Routes>
          <Route
            path="events/new"
            element={
              <EventEditor
                key="new"
                token={session.token}
                onLogout={onLogout}
              />
            }
          />
          <Route
            path="events/:id"
            element={
              <EventEditor
                key="edit"
                token={session.token}
                onLogout={onLogout}
              />
            }
          />
          <Route
            path="events"
            element={<Events token={session.token} onLogout={onLogout} />}
          />
          <Route
            path="profile"
            element={
              <>
                <p className="text-xs uppercase tracking-[0.2em] text-[#687674]">
                  Area riservata
                </p>
                <h1 className="mt-3 font-serif text-4xl">Il mio account</h1>
                <dl className="mt-8 max-w-xl space-y-6 rounded-xl border border-[#e1e5de] bg-white p-8">
                  {[
                    ["Nome", session.user.name],
                    ["Email", session.user.email],
                    ["Ruolo", "Amministratore"],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-sm text-[#687674]">{label}</dt>
                      <dd className="mt-1 break-words font-medium">{value}</dd>
                    </div>
                  ))}
                </dl>
              </>
            }
          />
          <Route path="*" element={<Navigate to="/admin/events" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export function Events({ token, onLogout }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(`${apiBase}/api/admin/events`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
          cache: "no-store",
        });
        if (response.status === 401) {
          onLogout();
          return;
        }
        if (!response.ok)
          throw new Error(
            response.status === 403
              ? "Non hai i permessi per consultare gli eventi."
              : "Impossibile caricare gli eventi. Riprova tra poco.",
          );
        const body = await response.json();
        if (!Array.isArray(body.data?.events))
          throw new Error("Il servizio ha restituito una risposta non valida.");
        if (!controller.signal.aborted) setEvents(body.data.events);
      } catch (failure) {
        if (!controller.signal.aborted)
          setError(
            failure instanceof TypeError
              ? "Il servizio non è raggiungibile. Controlla la connessione e riprova."
              : failure.message,
          );
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    load();
    return () => controller.abort();
  }, [token, onLogout, refresh]);

  const filtered = events.filter(
    (event) =>
      (status === "all" || event.status === status) &&
      [event.name, event.bride, event.groom, event.slug].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(search.trim().toLowerCase()),
      ),
  );
  function startTime(event) {
    if (!event.premiere_start_time) return "Da programmare";
    try {
      return new Intl.DateTimeFormat("it-IT", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: event.timeZone || "Europe/Rome",
      }).format(new Date(event.premiere_start_time));
    } catch {
      return "Data non disponibile";
    }
  }

  return (
    <>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#687674]">
            La tua regia
          </p>
          <h1 className="mt-3 font-serif text-4xl">Eventi première</h1>
          <p className="mt-3 text-[#687674]">
            Tutte le storie, dalla prima bozza alla proiezione.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/admin/events/new"
            className="rounded-lg bg-[#263b3e] px-5 py-3 text-sm text-white"
          >
            + Nuovo evento
          </Link>
          <button
            disabled={loading}
            onClick={() => setRefresh((value) => value + 1)}
            className="flex items-center gap-2 rounded-lg border border-[#cbd3cc] bg-white px-4 py-3 text-sm hover:bg-[#edf0e9] disabled:opacity-50"
          >
            <FiRefreshCw aria-hidden="true" />
            Aggiorna
          </button>
        </div>
      </header>
      <div className="my-8 grid grid-cols-2 gap-3 xl:grid-cols-4">
        {[
          ["Totale eventi", "all"],
          ["Bozze", "draft"],
          ["Pubblicati", "published"],
          ["Archiviati", "archived"],
        ].map(([label, key]) => (
          <div
            key={key}
            className="rounded-xl border border-[#e1e5de] bg-white p-5"
          >
            <p className="text-sm text-[#687674]">{label}</p>
            <p className="mt-3 font-serif text-3xl">
              {loading || error
                ? "—"
                : key === "all"
                  ? events.length
                  : events.filter((event) => event.status === key).length}
            </p>
          </div>
        ))}
      </div>
      <section
        aria-label="Elenco eventi"
        className="overflow-hidden rounded-xl border border-[#e1e5de] bg-white"
      >
        <div className="flex flex-wrap gap-4 border-b border-[#e1e5de] p-5">
          <div className="relative min-w-0 flex-1">
            <FiSearch
              aria-hidden="true"
              className="absolute left-3 top-3.5 text-[#687674]"
            />
            <label htmlFor="event-search" className="sr-only">
              Cerca eventi
            </label>
            <input
              id="event-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Cerca per nome, sposi o slug…"
              className="w-full rounded-lg border border-[#cbd3cc] py-2.5 pl-10 pr-3 text-sm"
            />
          </div>
          <label className="sr-only" htmlFor="event-status">
            Stato
          </label>
          <select
            id="event-status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="rounded-lg border border-[#cbd3cc] bg-white px-3 py-2.5 text-sm"
          >
            <option value="all">Tutti gli stati</option>
            {Object.entries(states).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        {loading ? (
          <p role="status" className="p-10 text-center text-[#687674]">
            Caricamento eventi…
          </p>
        ) : error ? (
          <div role="alert" className="p-10 text-center">
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => setRefresh((value) => value + 1)}
              className="mt-4 underline underline-offset-4"
            >
              Riprova
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <FiCalendar
              aria-hidden="true"
              className="mx-auto mb-4 text-3xl text-[#87958c]"
            />
            <h2 className="font-serif text-2xl">
              {events.length
                ? "Nessun risultato"
                : "La prossima storia inizia qui"}
            </h2>
            <p className="mt-2 text-sm text-[#687674]">
              {events.length
                ? "Prova un’altra ricerca o cambia il filtro."
                : "Non ci sono ancora eventi. Crea la tua prima bozza con Nuovo evento."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#f8f9f5] text-[#687674]">
                <tr>
                  {["Evento", "Première", "Stato", "Invito"].map((label) => (
                    <th
                      scope="col"
                      key={label}
                      className="px-5 py-4 font-medium"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((event) => (
                  <tr key={event._id} className="border-t border-[#edf0e9]">
                    <td className="px-5 py-5">
                      <p className="font-medium">
                        <Link
                          className="underline underline-offset-4"
                          to={`/admin/events/${event._id}`}
                        >
                          {event.name ||
                            [event.bride, event.groom]
                              .filter(Boolean)
                              .join(" & ") ||
                            "Evento senza titolo"}
                        </Link>
                      </p>
                      <p className="mt-1 text-xs text-[#687674]">
                        /{event.slug}
                      </p>
                    </td>
                    <td className="whitespace-nowrap px-5 py-5">
                      {startTime(event)}
                    </td>
                    <td className="px-5 py-5">
                      <span
                        className={`rounded-full px-3 py-1 text-xs ${badges[event.status] || badges.draft}`}
                      >
                        {states[event.status] || event.status}
                      </span>
                    </td>
                    <td className="px-5 py-5">
                      {event.status === "published" ? (
                        <Link
                          to={`/${encodeURIComponent(event.slug)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 underline underline-offset-4"
                          aria-label={`Apri invito ${event.slug} in una nuova scheda`}
                        >
                          Apri
                          <FiArrowUpRight aria-hidden="true" />
                        </Link>
                      ) : (
                        <span className="text-[#687674]">Non pubblico</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

